import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if we're on a native platform
    const platform = Capacitor.getPlatform();
    const supported = platform === 'ios' || platform === 'android';
    setIsSupported(supported);

    if (!supported || !user) return;

    const setupPushNotifications = async () => {
      try {
        // Request permission
        let permStatus = await PushNotifications.checkPermissions();
        
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
          console.log('Push notification permission not granted');
          return;
        }

        // Register for push notifications
        await PushNotifications.register();

        // Listen for registration success
        PushNotifications.addListener('registration', async (token: Token) => {
          console.log('Push registration success, token:', token.value);
          setToken(token.value);
          await saveToken(token.value, platform);
        });

        // Listen for registration errors
        PushNotifications.addListener('registrationError', (error) => {
          console.error('Push registration error:', error);
        });

        // Listen for push notifications received while app is in foreground
        PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
          console.log('Push notification received:', notification);
          toast(notification.title || 'New Notification', {
            description: notification.body,
          });
        });

        // Listen for push notification action (user tapped on notification)
        PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
          console.log('Push notification action performed:', action);
          // Navigate to relevant screen based on notification data
          const data = action.notification.data;
          if (data?.trip_id) {
            window.location.href = `/dashboard?tab=trips`;
          }
        });

      } catch (error) {
        console.error('Error setting up push notifications:', error);
      }
    };

    setupPushNotifications();

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [user]);

  const saveToken = async (tokenValue: string, platform: string) => {
    if (!user) return;

    try {
      // Upsert the token
      const { error } = await supabase
        .from('push_tokens')
        .upsert(
          {
            user_id: user.id,
            token: tokenValue,
            platform: platform as 'ios' | 'android' | 'web',
            is_active: true,
          },
          {
            onConflict: 'user_id,token',
          }
        );

      if (error) {
        console.error('Error saving push token:', error);
      } else {
        console.log('Push token saved successfully');
      }
    } catch (error) {
      console.error('Error saving push token:', error);
    }
  };

  const unregister = async () => {
    if (!user || !token) return;

    try {
      // Mark token as inactive
      await supabase
        .from('push_tokens')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .eq('token', token);

      await PushNotifications.unregister();
      setToken(null);
    } catch (error) {
      console.error('Error unregistering push notifications:', error);
    }
  };

  return {
    token,
    isSupported,
    unregister,
  };
};
